import React, { useMemo } from 'react';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend, ArcElement } from 'chart.js';
import { Bar, Line, Doughnut } from 'react-chartjs-2';
import { FaBolt, FaChartLine, FaHeartbeat, FaClock } from 'react-icons/fa';
import { StationStats, LoadTrend, SMPSDevice } from '../utils/smpsTypes';
import { SMPS_COLORS } from '../utils/smpsConstants';

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend, ArcElement);

interface SMPSChartsProps {
  stationStats: StationStats[];
  loadTrend: LoadTrend[];
  devices: SMPSDevice[];
}

const SMPSCharts: React.FC<SMPSChartsProps> = ({ stationStats, loadTrend, devices }) => {
  // DC Voltage by Station (Bar Chart)
  const barChartData = useMemo(() => ({
    labels: stationStats.map((s) => s.station),
    datasets: [
      {
        label: 'Avg Voltage (V)',
        data: stationStats.map((s) => s.avgVoltage),
        backgroundColor: stationStats.map((s) => {
          if (s.avgVoltage === 0) return SMPS_COLORS.statusOffline;
          if (s.avgVoltage < 44 || s.avgVoltage > 54) return SMPS_COLORS.statusCritical;
          if (s.avgVoltage < 46 || s.avgVoltage > 52) return SMPS_COLORS.statusWarning;
          return SMPS_COLORS.statusHealthy;
        }),
        borderRadius: 4,
      },
    ],
  }), [stationStats]);

  const barChartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          boxWidth: 12,
          padding: 8,
          font: { size: 11 }
        }
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { font: { size: 10 } }
      },
      y: {
        beginAtZero: false,
        min: 40,
        max: 56,
        grid: { color: 'rgba(0, 0, 0, 0.05)' },
        ticks: {
          font: { size: 10 },
          callback: (value: number | string) => `${value}V`,
        }
      },
    },
  }), []);

  // Load Current Trend (Line Chart)
  const lineChartData = useMemo(() => ({
    labels: loadTrend.map((t) => t.time),
    datasets: [
      {
        label: 'Load %',
        data: loadTrend.map((t) => t.loadPercent),
        borderColor: SMPS_COLORS.primary,
        backgroundColor: 'rgba(26, 82, 118, 0.1)',
        fill: true,
        tension: 0.4,
        pointRadius: 3,
        pointBackgroundColor: SMPS_COLORS.primary,
      },
    ],
  }), [loadTrend]);

  const lineChartOptions = useMemo(() => {
    const minLoad = Math.min(...loadTrend.map((t) => t.loadPercent));
    const dynamicMin = Math.max(0, Math.floor(minLoad / 10) * 10 - 10);

    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'top' as const,
          labels: {
            boxWidth: 12,
            padding: 8,
            font: { size: 11 }
          }
        },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: { font: { size: 10 } }
        },
        y: {
          beginAtZero: false,
          min: dynamicMin,
          max: 100,
          grid: { color: 'rgba(0, 0, 0, 0.05)' },
          ticks: {
            font: { size: 10 },
            callback: (value: number | string) => `${value}%`,
          },
        },
      },
    };
  }, [loadTrend]);

  // System Health Overview (Doughnut Chart)
  const healthCounts = useMemo(() => {
    const counts = { healthy: 0, warning: 0, critical: 0, offline: 0 };
    devices.forEach((device) => {
      switch (device.status) {
        case 'HEALTHY':
          counts.healthy++;
          break;
        case 'WARNING':
          counts.warning++;
          break;
        case 'CRITICAL':
          counts.critical++;
          break;
        case 'OFFLINE':
          counts.offline++;
          break;
      }
    });
    return counts;
  }, [devices]);

  const doughnutChartData = useMemo(() => ({
    labels: ['Healthy', 'Warning', 'Critical', 'Offline'],
    datasets: [
      {
        data: [healthCounts.healthy, healthCounts.warning, healthCounts.critical, healthCounts.offline],
        backgroundColor: [
          SMPS_COLORS.statusHealthy,
          SMPS_COLORS.statusWarning,
          SMPS_COLORS.statusCritical,
          SMPS_COLORS.statusOffline,
        ],
        borderWidth: 0,
      },
    ],
  }), [healthCounts]);

  const doughnutChartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom' as const,
        labels: {
          boxWidth: 12,
          padding: 8,
          font: { size: 11 }
        }
      },
    },
    cutout: '60%',
  }), []);

  const currentTime = new Date().toLocaleTimeString();

  return (
    <>
      {/* DC Voltage by Station */}
      <div className="chart-card">
        <div className="chart-title">
          <h3>
            <FaBolt />
            DC Voltage by Station
          </h3>
          <div className="refresh-indicator">
            <FaClock />
            <span>Updated: <span className="last-updated-time">{currentTime}</span></span>
          </div>
        </div>
        <div className="chart-container">
          <Bar data={barChartData} options={barChartOptions} />
        </div>
      </div>

      {/* Load Current Trend */}
      <div className="chart-card">
        <div className="chart-title">
          <h3>
            <FaChartLine />
            Load Current Trend
          </h3>
          <div className="refresh-indicator">
            <FaClock />
            <span>Updated: <span className="last-updated-time">{currentTime}</span></span>
          </div>
        </div>
        <div className="chart-container">
          <Line data={lineChartData} options={lineChartOptions} />
        </div>
      </div>

      {/* System Health Overview */}
      <div className="chart-card">
        <div className="chart-title">
          <h3>
            <FaHeartbeat />
            System Health Overview
          </h3>
          <div className="refresh-indicator">
            <FaClock />
            <span>Updated: <span className="last-updated-time">{currentTime}</span></span>
          </div>
        </div>
        <div className="chart-container">
          <Doughnut data={doughnutChartData} options={doughnutChartOptions} />
        </div>
      </div>
    </>
  );
};

export default SMPSCharts;
